import React from 'react';
import styled from 'styled-components';
import { ModalShell } from '../ui/shell';
import { Btn, Tag, TagDot } from '../ui/primitives';
import { formatAdminUserName } from '../services/adminUsersListApi';

const DetailGrid = styled.dl`
  display: grid;
  grid-template-columns: minmax(120px, 34%) 1fr;
  gap: 10px 16px;
  margin: 0;

  dt {
    margin: 0;
    font-size: ${({ theme }) => theme.fsMicro};
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: ${({ theme }) => theme.textSubtle};
  }

  dd {
    margin: 0;
    font-size: ${({ theme }) => theme.fsSm};
    color: ${({ theme }) => theme.text};
    line-height: 1.45;
    word-break: break-word;
  }
`;

const ModalFoot = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  width: 100%;
`;

function formatTimestamp(value) {
  if (!value) {
    return '—';
  }
  const numeric = Number(value);
  const date = Number.isFinite(numeric) && numeric > 0
    ? new Date(numeric)
    : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return String(value);
  }
  return date.toLocaleString();
}

/**
 * Read-only user detail sheet for Admin → Users and System → Application users.
 */
export default function AdminUserDetailSheet({
  open,
  detail,
  loading,
  error,
  onClose,
}) {
  if (!open) {
    return null;
  }

  const title = detail ? formatAdminUserName(detail) : 'User details';
  const subtitle = detail?.email || 'Review account information';

  return (
    <ModalShell
      title={title}
      sub={subtitle}
      max={560}
      onClose={onClose}
      foot={(
        <ModalFoot>
          <Btn $variant="ghost" onClick={onClose}>Close</Btn>
        </ModalFoot>
      )}
    >
      {loading ? <p>Loading user details…</p> : null}
      {error ? <p>{error}</p> : null}
      {!loading && !error && detail ? (
        <DetailGrid>
          <dt>Name</dt>
          <dd>{formatAdminUserName(detail)}</dd>

          <dt>Email</dt>
          <dd>{detail.email || '—'}</dd>

          <dt>Role</dt>
          <dd>{detail.role || '—'}</dd>

          <dt>Status</dt>
          <dd>
            <Tag $tone={detail.active === 1 ? 'green' : 'gray'}>
              <TagDot />
              {detail.active === 1 ? 'Active' : 'Inactive'}
            </Tag>
          </dd>

          {detail.enrollmentCount != null ? (
            <>
              <dt>Enrollments</dt>
              <dd>{detail.enrollmentCount}</dd>
            </>
          ) : null}

          {detail.emailVerified != null ? (
            <>
              <dt>Email verified</dt>
              <dd>{detail.emailVerified === 1 ? 'Yes' : 'No'}</dd>
            </>
          ) : null}

          {detail.joinedAt ? (
            <>
              <dt>Joined org</dt>
              <dd>{formatTimestamp(detail.joinedAt)}</dd>
            </>
          ) : null}

          {detail.createdAt ? (
            <>
              <dt>Account created</dt>
              <dd>{formatTimestamp(detail.createdAt)}</dd>
            </>
          ) : null}

          {detail.userId ? (
            <>
              <dt>User ID</dt>
              <dd>{detail.userId}</dd>
            </>
          ) : null}
        </DetailGrid>
      ) : null}
    </ModalShell>
  );
}
